import { Link } from "react-router-dom";
import {
  UserCog, Archive, MessageSquareText, Network, ShieldAlert, ArrowRightLeft, CalendarRange, Lock, ArrowRight,
} from "lucide-react";
import { Eyebrow, SectionTitle, SectionSub, PrimaryButton } from "@/components/web/atoms";
import { Reveal, EngineBackdrop, trackHomeEvent } from "@/components/web/home/primitives";

/* Section 12 — the premium Business / Role Intelligence story (the former
   enterprise-first hero message, now positioned here). */
const CAPS = [
  { icon: UserCog, label: "Role Intelligence" },
  { icon: Archive, label: "Employee knowledge continuity" },
  { icon: MessageSquareText, label: "Ask Previous Role" },
  { icon: Network, label: "Expertise mapping" },
  { icon: ShieldAlert, label: "Knowledge risk" },
  { icon: ArrowRightLeft, label: "Successor handoff" },
  { icon: CalendarRange, label: "30 / 60 / 90 onboarding" },
  { icon: Lock, label: "Enterprise permissions & governance" },
];

export default function BusinessIntelligenceSection() {
  return (
    <section
      id="business-intel"
      className="relative py-20 md:py-28 px-5 border-t border-[var(--w-hairline)] bg-[var(--w-bg2)] overflow-hidden scroll-mt-20"
      data-testid="home-business"
    >
      <EngineBackdrop />
      <div className="relative max-w-6xl mx-auto">
        <Reveal className="max-w-3xl">
          <Eyebrow tone="brand" className="mb-4">TeamNest for Business</Eyebrow>
          <SectionTitle>Your team's intelligence shouldn't disappear when people move on.</SectionTitle>
          <SectionSub className="mt-5">
            TeamNest captures what your people know, what they discuss, what meetings reveal, what documents
            contain, and what AI can reason across — so organizational knowledge grows instead of disappearing.
          </SectionSub>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {CAPS.map((c, i) => (
            <Reveal key={c.label} delay={(i % 4) * 60}>
              <div className="h-full rounded-[14px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-4 flex items-start gap-3">
                <span className="w-9 h-9 rounded-lg bg-[var(--w-brand-tint)] flex items-center justify-center shrink-0">
                  <c.icon className="w-4 h-4 text-[var(--w-brand)]" />
                </span>
                <span className="text-[14px] leading-5 text-[var(--w-text)] mt-1.5">{c.label}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={160} className="mt-10 rounded-[18px] border border-[var(--w-hairline)] bg-[var(--w-surface)] p-6 sm:p-7">
          <p className="text-[16px] sm:text-[18px] leading-7 text-[var(--w-text-dim)]">
            <span className="text-[var(--w-text)] font-semibold">For individuals, TeamNest organizes knowledge.</span>{" "}
            For businesses, TeamNest preserves it across the organization.
          </p>
          <div className="mt-6">
            <PrimaryButton
              as={Link}
              to="/business"
              data-testid="business-cta"
              onClick={() => trackHomeEvent("business-cta", { source: "business_section" })}
            >
              Explore TeamNest for Business <ArrowRight className="w-4 h-4" />
            </PrimaryButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
